import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Container, Typography } from "@mui/material";
import StaticPageLayout from "../../components/StaticPageLayout";
import * as styles from "./styles";

type TranslateFnT = (key: string, options?: Record<string, unknown>) => string;

type SummaryItemT = {
  term: string;
  description: string;
};

type BulletListPropsT = {
  items: string[];
};

const BulletList = ({ items }: BulletListPropsT) => (
  <Box component="ul" sx={{ pl: 0, listStyle: "none", mb: 2 }}>
    {items.map((item) => (
      <Box component="li" key={item} sx={{ display: "flex", gap: 1.5, mb: 1 }}>
        <Box component="span" sx={{ color: "#38bdf8", flexShrink: 0, mt: "2px" }}>
          •
        </Box>
        <Typography sx={{ ...styles.bodyText, mb: 0 }}>{item}</Typography>
      </Box>
    ))}
  </Box>
);

type PrivacyPolicyContentPropsT = {
  t: TranslateFnT;
};

const PrivacyPolicyContent = ({ t }: PrivacyPolicyContentPropsT) => {
  const summaryItems: SummaryItemT[] = [
    {
      term: t("summary.items.personalInformation.term"),
      description: t("summary.items.personalInformation.description"),
    },
    {
      term: t("summary.items.sensitiveInformation.term"),
      description: t("summary.items.sensitiveInformation.description"),
    },
    {
      term: t("summary.items.thirdParties.term"),
      description: t("summary.items.thirdParties.description"),
    },
    {
      term: t("summary.items.processing.term"),
      description: t("summary.items.processing.description"),
    },
    {
      term: t("summary.items.sharing.term"),
      description: t("summary.items.sharing.description"),
    },
    {
      term: t("summary.items.informationSafety.term"),
      description: t("summary.items.informationSafety.description"),
    },
    {
      term: t("summary.items.yourRights.term"),
      description: t("summary.items.yourRights.description"),
    },
    {
      term: t("summary.items.exercisingRights.term"),
      description: t("summary.items.exercisingRights.description"),
    },
  ];

  const tocItems: string[] = t("tableOfContents.items", { returnObjects: true }) as unknown as string[];
  const section1PersonalInfoItems: string[] = t("section1.personalInfoItems", {
    returnObjects: true,
  }) as unknown as string[];
  const section1AutoCollectedItems: string[] = t("section1.autoCollectedItems", {
    returnObjects: true,
  }) as unknown as string[];
  const section2Items: string[] = t("section2.items", { returnObjects: true }) as unknown as string[];
  const section3ThirdPartyItems: string[] = t("section3.thirdPartyItems", {
    returnObjects: true,
  }) as unknown as string[];
  const section3SituationItems: string[] = t("section3.situationItems", { returnObjects: true }) as unknown as string[];
  const section10CcpaCategories: string[] = t("section10.ccpaCategories", {
    returnObjects: true,
  }) as unknown as string[];
  const section10UseOfInfoItems: string[] = t("section10.useOfInfoItems", {
    returnObjects: true,
  }) as unknown as string[];

  return (
    <>
      <Box sx={styles.heroSection}>
        <Box sx={styles.heroGradient} />
        <Container maxWidth="md" sx={{ textAlign: "center", py: { xs: 14, md: 18 }, position: "relative", zIndex: 1 }}>
          <Typography sx={{ ...styles.eyebrow, mb: 3 }}>{t("hero.eyebrow")}</Typography>
          <Typography component="h1" sx={styles.heroTitle}>
            <Box component="span" sx={{ color: "white" }}>
              {t("hero.headlineWhite")}
            </Box>
            <Box component="span" sx={styles.gradientText}>
              {t("hero.headlineGradient")}
            </Box>
          </Typography>
          <Typography sx={styles.heroDate}>{t("hero.lastUpdated")}</Typography>
        </Container>
        <Box sx={styles.heroFade} />
      </Box>

      <Box sx={{ background: "#080f1e", py: { xs: 8, md: 12 } }}>
        <Container maxWidth="md">
          <Typography sx={{ ...styles.bodyText, mb: 6 }}>
            {t("intro.body")}{" "}
            <Box component="a" href="mailto:compliance@powur.com" sx={styles.emailLink}>
              compliance@powur.com
            </Box>
            .
          </Typography>

          <Typography component="h2" sx={{ ...styles.sectionTitle, fontSize: { xs: "1.3rem", md: "1.6rem" } }}>
            {t("summary.title")}
          </Typography>

          <Box sx={styles.summaryGrid}>
            {summaryItems.map((item) => (
              <Box key={item.term} sx={styles.glass}>
                <Typography sx={styles.summaryTermTitle}>{item.term}</Typography>
                <Typography sx={{ ...styles.bodyText, mb: 0, fontSize: "0.9rem" }}>{item.description}</Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ ...styles.glass, mb: 8 }}>
            <Typography sx={styles.tocTitle}>{t("tableOfContents.title")}</Typography>
            <Box component="ol" sx={{ pl: 0, listStyle: "none", m: 0 }}>
              {tocItems.map((item, i) => (
                <Box component="li" key={item} sx={{ display: "flex", gap: 2, mb: 1.5 }}>
                  <Typography sx={styles.tocNumber}>{String(i + 1).padStart(2, "0")}.</Typography>
                  <Typography sx={styles.tocItemText}>{item}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Box id="section-1" sx={{ mb: 8 }}>
            <Typography component="h2" sx={styles.sectionTitle}>
              {t("section1.heading")}
            </Typography>
            <Typography sx={styles.bodyText}>{t("section1.intro")}</Typography>
            <Typography sx={{ ...styles.bodyText, fontWeight: 600, color: "white" }}>
              {t("section1.personalInfoSubheading")}
            </Typography>
            <Typography sx={styles.bodyText}>{t("section1.personalInfoBody")}</Typography>
            <BulletList items={section1PersonalInfoItems} />
            <Typography sx={styles.bodyText}>
              <Box component="span" sx={{ color: "white", fontWeight: 600 }}>
                {t("section1.sensitiveInfoLabel")}
              </Box>{" "}
              {t("section1.sensitiveInfoBody")}
            </Typography>
            <Typography sx={styles.bodyText}>{t("section1.accuracyNote")}</Typography>
            <Typography sx={{ ...styles.bodyText, fontWeight: 600, color: "white" }}>
              {t("section1.autoCollectedSubheading")}
            </Typography>
            <Typography sx={styles.bodyText}>{t("section1.autoCollectedBody")}</Typography>
            <BulletList items={section1AutoCollectedItems} />
          </Box>

          <Box id="section-2" sx={{ mb: 8 }}>
            <Typography component="h2" sx={styles.sectionTitle}>
              {t("section2.heading")}
            </Typography>
            <Typography sx={styles.bodyText}>{t("section2.intro")}</Typography>
            <BulletList items={section2Items} />
          </Box>

          <Box id="section-3" sx={{ mb: 8 }}>
            <Typography component="h2" sx={styles.sectionTitle}>
              {t("section3.heading")}
            </Typography>
            <Typography sx={styles.bodyText}>{t("section3.intro")}</Typography>
            <BulletList items={section3ThirdPartyItems} />
            <Typography sx={styles.bodyText}>{t("section3.situationsIntro")}</Typography>
            <BulletList items={section3SituationItems} />
          </Box>

          <Box id="section-4" sx={{ mb: 8 }}>
            <Typography component="h2" sx={styles.sectionTitle}>
              {t("section4.heading")}
            </Typography>
            <Typography sx={styles.bodyText}>{t("section4.body")}</Typography>
          </Box>

          <Box id="section-5" sx={{ mb: 8 }}>
            <Typography component="h2" sx={styles.sectionTitle}>
              {t("section5.heading")}
            </Typography>
            <Typography sx={styles.bodyText}>{t("section5.body1")}</Typography>
            <Typography sx={styles.bodyText}>{t("section5.body2")}</Typography>
          </Box>

          <Box id="section-6" sx={{ mb: 8 }}>
            <Typography component="h2" sx={styles.sectionTitle}>
              {t("section6.heading")}
            </Typography>
            <Typography sx={styles.bodyText}>{t("section6.body")}</Typography>
          </Box>

          <Box id="section-7" sx={{ mb: 8 }}>
            <Typography component="h2" sx={styles.sectionTitle}>
              {t("section7.heading")}
            </Typography>
            <Typography sx={styles.bodyText}>{t("section7.body")}</Typography>
          </Box>

          <Box id="section-8" sx={{ mb: 8 }}>
            <Typography component="h2" sx={styles.sectionTitle}>
              {t("section8.heading")}
            </Typography>
            <Typography sx={{ ...styles.bodyText, fontWeight: 600, color: "white" }}>
              {t("section8.withdrawalSubheading")}
            </Typography>
            <Typography sx={styles.bodyText}>{t("section8.withdrawalBody")}</Typography>
            <Typography sx={{ ...styles.bodyText, fontWeight: 600, color: "white" }}>
              {t("section8.optOutSubheading")}
            </Typography>
            <Typography sx={styles.bodyText}>{t("section8.optOutBody")}</Typography>
            <Typography sx={{ ...styles.bodyText, fontWeight: 600, color: "white" }}>
              {t("section8.accountSubheading")}
            </Typography>
            <Typography sx={styles.bodyText}>{t("section8.accountBody")}</Typography>
            <Typography sx={{ ...styles.bodyText, fontWeight: 600, color: "white" }}>
              {t("section8.cookiesSubheading")}
            </Typography>
            <Typography sx={styles.bodyText}>{t("section8.cookiesBody")}</Typography>
            <Typography sx={styles.bodyText}>
              {t("section8.questionsBody")}{" "}
              <Box component="a" href="mailto:compliance@powur.com" sx={styles.emailLink}>
                compliance@powur.com
              </Box>
              .
            </Typography>
          </Box>

          <Box id="section-9" sx={{ mb: 8 }}>
            <Typography component="h2" sx={styles.sectionTitle}>
              {t("section9.heading")}
            </Typography>
            <Typography sx={styles.bodyText}>{t("section9.body")}</Typography>
          </Box>

          <Box id="section-10" sx={{ mb: 8 }}>
            <Typography component="h2" sx={styles.sectionTitle}>
              {t("section10.heading")}
            </Typography>
            <Typography sx={styles.bodyText}>{t("section10.intro")}</Typography>
            <Typography sx={{ ...styles.bodyText, fontWeight: 600, color: "white" }}>
              {t("section10.ccpaSubheading")}
            </Typography>
            <Typography sx={styles.bodyText}>{t("section10.ccpaDefinition")}</Typography>
            <Typography sx={styles.bodyText}>{t("section10.ccpaCollectionIntro")}</Typography>
            <BulletList items={section10CcpaCategories} />
            <Typography sx={{ ...styles.bodyText, fontWeight: 600, color: "white" }}>
              {t("section10.useOfInfoSubheading")}
            </Typography>
            <Typography sx={styles.bodyText}>{t("section10.useOfInfoIntro")}</Typography>
            <BulletList items={section10UseOfInfoItems} />
            <Typography sx={{ ...styles.bodyText, fontWeight: 600, color: "white" }}>
              {t("section10.sharingSubheading")}
            </Typography>
            <Typography sx={styles.bodyText}>{t("section10.sharingBody1")}</Typography>
            <Typography sx={styles.bodyText}>{t("section10.sharingBody2")}</Typography>
            <Typography sx={styles.bodyText}>{t("section10.sharingBody3")}</Typography>
            <Typography sx={{ ...styles.bodyText, fontWeight: 600, color: "white" }}>
              {t("section10.rightsSubheading")}
            </Typography>
            <Typography sx={styles.bodyText}>{t("section10.rightsBody")}</Typography>
            <Typography sx={styles.bodyText}>
              {t("section10.deletionBody")}{" "}
              <Box component="a" href="mailto:compliance@powur.com" sx={styles.emailLink}>
                Compliance@powur.com
              </Box>
              .
            </Typography>
            <Typography sx={{ ...styles.bodyText, fontWeight: 600, color: "white" }}>
              {t("section10.nonDiscriminationSubheading")}
            </Typography>
            <Typography sx={styles.bodyText}>{t("section10.nonDiscriminationBody")}</Typography>
          </Box>

          <Box id="section-11" sx={{ mb: 8 }}>
            <Typography component="h2" sx={styles.sectionTitle}>
              {t("section11.heading")}
            </Typography>
            <Typography sx={styles.bodyText}>{t("section11.body")}</Typography>
          </Box>

          <Box id="section-12" sx={{ mb: 8 }}>
            <Typography component="h2" sx={styles.sectionTitle}>
              {t("section12.heading")}
            </Typography>
            <Typography sx={styles.bodyText}>
              {t("section12.intro")}{" "}
              <Box component="a" href="mailto:compliance@powur.com" sx={styles.emailLink}>
                compliance@powur.com
              </Box>{" "}
              {t("section12.postIntro")}
            </Typography>
            <Box sx={{ ...styles.glass, display: "inline-block", mt: 1 }}>
              <Typography sx={styles.addressBlock}>
                {t("section12.address.company")}
                <br />
                {t("section12.address.careOf")}
                <br />
                {t("section12.address.street")}
                <br />
                {t("section12.address.unit")}
                <br />
                {t("section12.address.city")}
              </Typography>
            </Box>
          </Box>

          <Box id="section-13" sx={{ mb: 4 }}>
            <Typography component="h2" sx={styles.sectionTitle}>
              {t("section13.heading")}
            </Typography>
            <Typography sx={styles.bodyText}>
              {t("section13.body")}{" "}
              <Box component="a" href="mailto:compliance@powur.com" sx={styles.emailLink}>
                compliance@powur.com
              </Box>
              .
            </Typography>
          </Box>
        </Container>
      </Box>
    </>
  );
};

const PrivacyPolicyPage = () => {
  const { t } = useTranslation("static_privacy_policy");
  return (
    <StaticPageLayout
      title={t("meta.title")}
      description={t("meta.description")}
      keywords={t("meta.keywords")}
      ogTitle={t("meta.ogTitle")}
      ogDescription={t("meta.ogDescription")}
      twitterTitle={t("meta.twitterTitle")}
      twitterDescription={t("meta.twitterDescription")}
    >
      <PrivacyPolicyContent t={t} />
    </StaticPageLayout>
  );
};

export default PrivacyPolicyPage;
